import React from 'react';
import { default as ReactSelect, SingleValue, StylesConfig } from 'react-select';
import type { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import styled from 'styled-components';
import type * as CSS from 'csstype';
import { devlog } from 'utilities';
import { Label } from 'shared/Typhography/InputStyled';

import { breakpoints } from '../../../theme';

export interface Option<Value = string | number | boolean, Label = string | React.ReactNode> {
  readonly value: Value;
  readonly label: Label;
  readonly searchValue?: string; // Added to make complex labels easier to search.
}

export interface SelectProps {
  className?: string;
  options: Option[] | undefined;
  name?: string;
  width?: string;
  value?: SingleValue<Option>;
  placeholder?: string;
  isDisabled?: boolean;
  maxMenuHeight?: number;
  style?: any;
  defaultValue?: SingleValue<Option>;
  includeCustomSearch?: boolean;
  label?: string;
  /** Id for label's htmlFor attribute. */
  id?: string;
  filterOption?: ((option: FilterOptionOption<Option>, inputValue: string) => boolean) | null;
  onChange: (option: SingleValue<Option>) => void;
  onKeyPress?: (event?: any) => void;
  isClearable?: boolean;
  isSearchable?: boolean;
}

export interface SelectContainerProps {
  width?: string;
}

const SelectContainer = styled.div<SelectContainerProps>`
  width: ${({ width }) => (width ? width : '300px')};
  @media (${breakpoints.lg}) {
    width: 100%;
  }
`;

const style: StylesConfig = {
  control: (base) => ({
    ...base,
    '&:hover': { boxShadow: '0px 0px 10px #c4c4c4', transition: '0.3s' },
    boxShadow: '0px 0px 3px #c4c4c4',
    border: 0,
    borderRadius: '35px',
    textAlign: 'center',
    width: '100%',
    height: '50px',
  }),
  option: (base, { isSelected, isFocused }) => {
    let backgroundColor: CSS.Property.BackgroundColor | undefined;

    // Show proper hover and selected styles.
    if (isSelected && isFocused) {
      backgroundColor = isSelected && isFocused ? '#585CEA77' : backgroundColor;
    } else if (isFocused) {
      backgroundColor = isFocused ? '#00000011' : backgroundColor;
    } else if (isSelected) {
      backgroundColor = isSelected ? '#585CEA' : undefined;
    }

    return { ...base, backgroundColor };
  },
};

/**
 * @todo: Trigger Select input on label click.
 */
export const Select = (props: SelectProps) => {
  if (props.label && !props.id) {
    console.warn('Id must also be defined when label is present');
  }

  return (
    <SelectContainer
      className={props.className}
      width={props?.width}
      style={{ position: 'relative', ...props.style }}
    >
      {props.label && <Label htmlFor={props.id}>{props.label}</Label>}
      <ReactSelect
        {...props}
        styles={
          style as any /* any because this ReactSelect wrapper component's props weren't typed properly from the beginning */
        }
        filterOption={
          props.includeCustomSearch ? props.filterOption || customSearchFilter : undefined
        }
      />
    </SelectContainer>
  );
};

/**
 * Because artists label contains profile image it messes up search functionality,
 * we added this simple custom search to bypass it, it is put here because it may be
 * reused in future for similar purposes.
 *
 * Note: For this function to work you need to add "searchValue" field to your Select's options.
 *
 * @param option Option to compare (passed in by react-select).
 * @param inputValue  Value of search input.
 */
function customSearchFilter(option: FilterOptionOption<Option>, inputValue: string): boolean {
  try {
    // Replace regex special characters with literals to prevent malformed RegExp creation
    // List of characters that needs escaping can be found e.g. here:
    // https://www3.ntu.edu.sg/home/ehchua/programming/howto/Regexe.html#zz-2.2
    inputValue = inputValue.replaceAll(/([.[\]^$+*?{}()|\\])/g, '\\$1');
    const regex = new RegExp(inputValue, 'i');
    return option.data.searchValue?.match(regex) ? true : false;
  } catch (e) {
    devlog('error', 'Error while searching in Select: ', e);
  }

  return false;
}
